import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BookOpen, ArrowLeft, Tag, Calendar, Clock, ArrowRight } from "lucide-react";
import api from "../../lib/api";
import useSeo from "../../hooks/useSeo";

export default function BlogDetail() {
    const { slug } = useParams();
    const { i18n } = useTranslation();
    const lang = (i18n.language || "tr").split("-")[0];
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    useEffect(() => {
        api.get(`/blog/${slug}`)
            .then(({ data }) => setPost(data))
            .catch((e) => setErr(e?.response?.status === 404 ? "Yazı bulunamadı" : "Yüklenemedi"))
            .finally(() => setLoading(false));
    }, [slug]);

    const tr = (obj) => (obj && (obj[lang] || obj.tr)) || "";

    useSeo({
        title: post?.seo?.meta_title || tr(post?.title) || "Blog Yazısı",
        description: post?.seo?.meta_desc || tr(post?.excerpt) || "",
        path: `/blog/${slug}`,
        ogImage: post?.featured_image || post?.seo?.og_image,
        structuredData: post
            ? {
                  "@context": "https://schema.org",
                  "@type": "Article",
                  headline: tr(post.title),
                  description: tr(post.excerpt),
                  datePublished: post.published_at,
                  author: { "@type": "Person", name: post.author?.name || "Otel Pro Editör" },
                  image: post.featured_image || undefined,
                  publisher: {
                      "@type": "Organization",
                      name: "Otel Pro",
                      logo: { "@type": "ImageObject", url: "https://customer-assets.emergentagent.com/job_otel-central/artifacts/tvucn0yf_wordmark-light.svg" },
                  },
              }
            : undefined,
    });

    return (
        <div className="bg-light-bg min-h-[80vh]" data-testid="blog-detail-page">
            <section className="bg-dark-navy text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#08111d] via-[#0c2438] to-[#123b52]" />
                <div className="relative max-w-4xl mx-auto px-6 py-12 lg:py-16">
                    <Link to="/blog" className="text-xs uppercase tracking-[0.2em] text-teal font-semibold inline-flex items-center gap-1 hover:text-white">
                        <ArrowLeft className="w-3.5 h-3.5" /> Blog
                    </Link>
                    {loading ? (
                        <div className="mt-6 text-white/70 text-sm">Yükleniyor...</div>
                    ) : err ? (
                        <h1 className="mt-3 text-3xl font-bold">{err}</h1>
                    ) : (
                        <>
                            {post.category && (
                                <span className="mt-4 inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-gold font-semibold">
                                    <Tag className="w-3 h-3" /> {tr(post.category.name)}
                                </span>
                            )}
                            <h1 className="mt-2 text-3xl sm:text-5xl font-bold tracking-tight max-w-3xl">{tr(post.title)}</h1>
                            <p className="mt-3 text-white/75 max-w-2xl">{tr(post.excerpt)}</p>
                            <div className="mt-5 flex items-center gap-5 text-xs text-white/65 font-data">
                                <span className="inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {post.published_at ? new Date(post.published_at).toLocaleDateString("tr-TR") : ""}</span>
                                <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {post.reading_time || 4} dk okuma</span>
                                {post.author?.name && <span>{post.author.name}</span>}
                            </div>
                        </>
                    )}
                </div>
            </section>

            {!loading && !err && post && (
                <>
                    {post.featured_image && (
                        <div className="max-w-4xl mx-auto px-6 -mt-10 relative z-10">
                            <img src={post.featured_image} alt={tr(post.title)} className="w-full rounded-lg border border-slate-200 shadow-2xl" />
                        </div>
                    )}

                    <article className="max-w-3xl mx-auto px-6 py-12" data-testid="blog-article">
                        <div
                            className="prose prose-slate max-w-none prose-headings:text-dark-navy prose-a:text-teal-700 prose-strong:text-dark-navy prose-p:text-deep-blue/85 prose-p:leading-relaxed"
                            dangerouslySetInnerHTML={{ __html: tr(post.content) }}
                        />
                    </article>

                    {post.related_posts?.length > 0 && (
                        <section className="bg-white py-12 border-t border-slate-200">
                            <div className="max-w-5xl mx-auto px-6">
                                <h2 className="text-lg font-bold text-dark-navy mb-5">İlgili Yazılar</h2>
                                <div className="grid sm:grid-cols-3 gap-4" data-testid="blog-related">
                                    {post.related_posts.map((p) => (
                                        <Link
                                            key={p.id}
                                            to={`/blog/${p.slug}`}
                                            className="lift-card group bg-light-bg rounded-lg overflow-hidden hover:bg-white border border-transparent hover:border-teal/40"
                                        >
                                            <div className="aspect-[16/9] bg-deep-blue/10 relative overflow-hidden">
                                                {p.featured_image ? (
                                                    <img src={p.featured_image} alt={tr(p.title)} className="w-full h-full object-cover" />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center text-white/30 bg-gradient-to-br from-deep-blue to-dark-navy">
                                                        <BookOpen className="w-8 h-8" />
                                                    </div>
                                                )}
                                            </div>
                                            <div className="p-4">
                                                <h3 className="text-sm font-bold text-dark-navy line-clamp-2">{tr(p.title)}</h3>
                                                <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-teal-700">
                                                    Devamını oku <ArrowRight className="w-3 h-3" />
                                                </span>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </div>
                        </section>
                    )}
                </>
            )}
        </div>
    );
}
